import React from 'react';
import { createPortal } from 'react-dom';

interface NimbusModalProps {
  children: React.ReactNode;
  size?: 'sm' | 'lg' | 'xl';
  centered?: boolean;
  scrollable?: boolean;
  onDialogClick?: React.MouseEventHandler<HTMLDivElement>;
}

export const NimbusModal: React.FC<NimbusModalProps> = ({
  children,
  size,
  centered = false,
  scrollable = false,
  onDialogClick,
}) => {
  const dialogClass = [
    'modal-dialog',
    size ? `modal-${size}` : '',
    centered ? 'modal-dialog-centered' : '',
    scrollable ? 'modal-dialog-scrollable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="nimbus-modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
      {/* Modal — mirrors Bootstrap's .modal.show structure */}
      <div
        className="modal show nimbus-modal-portal"
        tabIndex={-1}
        role="dialog"
        style={{ position: 'fixed', inset: 0, display: 'block', overflowX: 'hidden', overflowY: 'auto' }}
      >
        <div className={dialogClass} role="document" onClick={onDialogClick}>
          {children}
        </div>
      </div>
    </>,
    document.body
  );
};

/**
 * NimbusModalContent — the modal-content/header/body/footer boilerplate every NimbusModal
 * consumer previously hand-wrote verbatim (modal-header > h5.modal-title + btn-close,
 * modal-body wrapping the form, modal-footer wrapping the action buttons). Composes inside
 * NimbusModal exactly where that boilerplate used to sit:
 *
 *   <NimbusModal>
 *     <NimbusModalContent title="Edit Department" onClose={closeModal} footer={<>...buttons...</>}>
 *       ...form fields...
 *     </NimbusModalContent>
 *   </NimbusModal>
 *
 * Still just markup, not new behavior - callers keep owning their own state/mutations exactly
 * as before, so this doesn't change what any existing modal does, only how much of its own
 * chrome it has to write.
 */
interface NimbusModalContentProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const NimbusModalContent: React.FC<NimbusModalContentProps> = ({ title, subtitle, onClose, children, footer }) => (
  <div className="modal-content">
    <div className="modal-header">
      <div>
        <h5 className="modal-title">{title}</h5>
        {subtitle && <p className="np-sub mb-0 mt-1">{subtitle}</p>}
      </div>
      <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
    </div>
    <div className="modal-body">{children}</div>
    {footer && <div className="modal-footer">{footer}</div>}
  </div>
);
